import { useCallback, useEffect, useMemo, useState } from 'react'
import { dispatchThemeChange } from '@/components/theme-provider'
import { createLogger } from '@/lib/logger'
import { useAuth } from '@/store/auth.store'

import {
  PreferenceContextEnum,
  PreferenceScopeEnum,
  SidebarTheme,
  ThemePreferences,
} from '@scrambl/shared'

import { useOrganizationStore } from '../store/organization.store'
import {
  SIDEBAR_THEMES,
  useUIPreferencesStore,
} from '../store/ui-preferences.store'
import { useUserPreferencesStore } from '../store/user-preferences.store'

const logger = createLogger('hooks/useThemePreferences')

export type ThemeMode = 'light' | 'dark' | 'system'

// localStorage keys for instant theme application (prevents flash). Both the
// light/dark mode and the sidebar theme are synced per user/org and cached
// under user/org keys until the synced preference loads.
const THEME_MODE_STORAGE_PREFIX = 'scrambl-ui-theme-user-org'
const SIDEBAR_THEME_STORAGE_PREFIX = 'scrambl-sidebar-theme-user-org'
// Read by index.html and ThemeProvider before any org is known.
const BOOT_THEME_MODE_KEY = 'scrambl-ui-theme'
// Device-local mode from before modes were synced per org. Read-only: it seeds
// orgs where the user has not picked a mode yet.
const LEGACY_USER_THEME_MODE_PREFIX = 'scrambl-ui-theme-user'
const LEGACY_ORG_THEME_MODE_PREFIX = 'scrambl-ui-theme'
// Global fallback key (used when no org is selected)
const THEME_MODE_GLOBAL_KEY = 'scrambl-ui-theme-global'
const SIDEBAR_THEME_GLOBAL_KEY = 'scrambl-sidebar-theme-global'

// Default values
const DEFAULT_THEME_MODE: ThemeMode = 'system'
const DEFAULT_SIDEBAR_THEME: SidebarTheme = 'cast-iron-skillet'

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system'
}

function getThemeModeKey(
  userId: string | undefined,
  orgId: string | undefined,
): string {
  return userId && orgId
    ? `${THEME_MODE_STORAGE_PREFIX}-${userId}-${orgId}`
    : THEME_MODE_GLOBAL_KEY
}

/** The mode this device used before modes were synced per org. */
function getLegacyThemeMode(
  userId: string | undefined,
  orgId: string | undefined,
): ThemeMode | null {
  const candidates = [
    userId ? `${LEGACY_USER_THEME_MODE_PREFIX}-${userId}` : null,
    orgId ? `${LEGACY_ORG_THEME_MODE_PREFIX}-${orgId}` : null,
  ]
  for (const key of candidates) {
    const stored = key ? localStorage.getItem(key) : null
    if (isThemeMode(stored)) return stored
  }
  return null
}

function applyThemeModeClass(mode: ThemeMode) {
  const root = document.documentElement
  root.classList.remove('dark', 'light')
  if (mode === 'system') {
    root.classList.add(
      window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light',
    )
  } else {
    root.classList.add(mode)
  }
}

function getSidebarThemeKey(
  userId: string | undefined,
  orgId: string | undefined,
): string {
  return userId && orgId
    ? `${SIDEBAR_THEME_STORAGE_PREFIX}-${userId}-${orgId}`
    : SIDEBAR_THEME_GLOBAL_KEY
}

interface UseThemePreferencesReturn {
  // Current values
  themeMode: ThemeMode
  sidebarTheme: SidebarTheme

  // Computed values
  isDarkMode: boolean
  currentSidebarColor: string

  // Actions
  setThemeMode: (mode: ThemeMode) => Promise<void>
  setSidebarTheme: (theme: SidebarTheme) => Promise<void>

  // Loading state
  isLoading: boolean
}

/**
 * Hook for managing per-user, per-organization appearance preferences.
 *
 * Light/dark/system mode and the sidebar theme are synced at USER scope inside
 * the current organization, so each org a user belongs to keeps its own look.
 * Orgs where the user never picked a mode fall back to this device's
 * pre-existing mode choice.
 *
 * For instant theme application (to prevent flash on load), values are also
 * cached in localStorage under user/org keys, updated whenever the theme
 * changes or the user switches organizations. `ThemePreferencesSync` mounts
 * this hook app-wide so the current org's appearance is always applied.
 */
export function useThemePreferences(): UseThemePreferencesReturn {
  const currentOrgId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const currentUserId = useAuth(state => state.user?.id)

  // Store selectors
  const isStoreInitialized = useUserPreferencesStore(
    state => state.isInitialized,
  )
  const storeIsLoading = useUserPreferencesStore(state => state.isLoading)
  const updatePreference = useUserPreferencesStore(
    state => state.updatePreference,
  )
  const storedPreferences = useUserPreferencesStore(state => state.preferences)
  const setUISidebarTheme = useUIPreferencesStore(
    state => state.setSidebarTheme,
  )

  // Optimistic state for immediate UI updates (checkmarks, etc.)
  const [optimisticThemeMode, setOptimisticThemeMode] =
    useState<ThemeMode | null>(null)
  const [optimisticSidebarTheme, setOptimisticSidebarTheme] =
    useState<SidebarTheme | null>(null)

  // Get the user-scoped theme color preference for this organization.
  const rawPreferenceData = useMemo(() => {
    if (!isStoreInitialized) {
      return undefined
    }
    return [...storedPreferences]
      .filter(
        preference =>
          preference.context === PreferenceContextEnum.THEME &&
          preference.scope === PreferenceScopeEnum.USER &&
          (preference.moduleType === null ||
            preference.moduleType === undefined),
      )
      .sort((a, b) => {
        const aUpdated = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
        const bUpdated = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
        if (aUpdated !== bUpdated) return bUpdated - aUpdated
        const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0
        const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0
        return bCreated - aCreated
      })[0]
  }, [isStoreInitialized, storedPreferences])

  // Extract theme values with fallbacks
  const storedThemeMode: ThemeMode = useMemo(() => {
    const prefs = rawPreferenceData?.preferences as ThemePreferences | undefined
    if (isThemeMode(prefs?.mode)) return prefs.mode

    if (!isStoreInitialized) {
      // Before preferences load, use this user/org's cached mode to avoid a flash.
      const cached = localStorage.getItem(
        getThemeModeKey(currentUserId, currentOrgId),
      )
      if (isThemeMode(cached)) return cached
    }

    return getLegacyThemeMode(currentUserId, currentOrgId) ?? DEFAULT_THEME_MODE
  }, [rawPreferenceData, isStoreInitialized, currentOrgId, currentUserId])

  const storedSidebarTheme: SidebarTheme = useMemo(() => {
    // If we have a preference from the backend, use it
    if (rawPreferenceData?.preferences) {
      const prefs = rawPreferenceData.preferences as unknown as ThemePreferences
      if (prefs.sidebarTheme) return prefs.sidebarTheme
    }

    if (isStoreInitialized) {
      return DEFAULT_SIDEBAR_THEME
    }

    // Only fall back to localStorage during initial render (before preferences loaded)
    // This prevents flash on page load - use user/org-specific key.
    const stored = localStorage.getItem(
      getSidebarThemeKey(currentUserId, currentOrgId),
    )
    if (stored && SIDEBAR_THEMES.some(t => t.id === stored)) {
      return stored as SidebarTheme
    }
    return DEFAULT_SIDEBAR_THEME
  }, [rawPreferenceData, isStoreInitialized, currentOrgId, currentUserId])

  // Use optimistic values if set, otherwise use stored values
  const themeMode = optimisticThemeMode ?? storedThemeMode
  const sidebarTheme = optimisticSidebarTheme ?? storedSidebarTheme

  // Clear optimistic state when stored values catch up
  useEffect(() => {
    if (
      optimisticThemeMode !== null &&
      storedThemeMode === optimisticThemeMode
    ) {
      setOptimisticThemeMode(null)
    }
  }, [storedThemeMode, optimisticThemeMode])

  useEffect(() => {
    if (
      optimisticSidebarTheme !== null &&
      storedSidebarTheme === optimisticSidebarTheme
    ) {
      setOptimisticSidebarTheme(null)
    }
  }, [storedSidebarTheme, optimisticSidebarTheme])

  // Compute isDarkMode
  const isDarkMode = useMemo(() => {
    if (themeMode === 'dark') return true
    if (themeMode === 'light') return false
    // For 'system', check the media query
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return false
  }, [themeMode])

  // Get current sidebar color based on theme
  const currentSidebarColor = useMemo(() => {
    const themeConfig =
      SIDEBAR_THEMES.find(t => t.id === sidebarTheme) || SIDEBAR_THEMES[0]
    return isDarkMode ? themeConfig.darkColor : themeConfig.lightColor
  }, [sidebarTheme, isDarkMode])

  // Apply theme mode to DOM
  useEffect(() => {
    applyThemeModeClass(themeMode)

    // Cache for this user/org, and as the boot mode for the next cold load.
    if (currentUserId && currentOrgId) {
      localStorage.setItem(
        getThemeModeKey(currentUserId, currentOrgId),
        themeMode,
      )
    }
    localStorage.setItem(BOOT_THEME_MODE_KEY, themeMode)

    // Keep ThemeProvider (useTheme consumers) in step after an org switch.
    dispatchThemeChange(themeMode)
  }, [themeMode, currentOrgId, currentUserId])

  // Apply sidebar theme to DOM
  useEffect(() => {
    document.documentElement.setAttribute('data-sidebar-theme', sidebarTheme)
    setUISidebarTheme(sidebarTheme)

    // Cache in user/org-specific localStorage for next load
    localStorage.setItem(
      getSidebarThemeKey(currentUserId, currentOrgId),
      sidebarTheme,
    )
  }, [sidebarTheme, currentOrgId, currentUserId, setUISidebarTheme])

  // Listen for system theme changes when mode is 'system'
  useEffect(() => {
    if (themeMode !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      const root = document.documentElement
      root.classList.remove('dark', 'light')
      root.classList.add(mediaQuery.matches ? 'dark' : 'light')
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [themeMode])

  // Merge into this user's theme record for the current organization.
  const saveThemePreference = useCallback(
    async (patch: Partial<ThemePreferences>) => {
      try {
        await updatePreference(
          PreferenceContextEnum.THEME,
          current => ({
            ...(current as Partial<ThemePreferences> | undefined),
            ...patch,
          }),
          undefined,
          PreferenceScopeEnum.USER,
        )
      } catch (error) {
        logger.error('[useThemePreferences] Error saving theme:', error)
      }
    },
    [updatePreference],
  )

  // Save theme mode
  const setThemeMode = useCallback(
    async (mode: ThemeMode) => {
      // Set optimistic state for immediate UI update (checkmarks, etc.)
      setOptimisticThemeMode(mode)

      // Immediately apply to DOM and the user/org cache.
      applyThemeModeClass(mode)
      if (currentUserId && currentOrgId) {
        localStorage.setItem(getThemeModeKey(currentUserId, currentOrgId), mode)
      }
      localStorage.setItem(BOOT_THEME_MODE_KEY, mode)

      // Notify ThemeProvider to update its state (for components using useTheme)
      dispatchThemeChange(mode)

      await saveThemePreference({ mode })
    },
    [currentOrgId, currentUserId, saveThemePreference],
  )

  // Save sidebar theme
  const setSidebarTheme = useCallback(
    async (theme: SidebarTheme) => {
      // Set optimistic state for immediate UI update (checkmarks, etc.)
      setOptimisticSidebarTheme(theme)

      // Immediately apply to DOM and org-specific localStorage
      document.documentElement.setAttribute('data-sidebar-theme', theme)
      setUISidebarTheme(theme)
      localStorage.setItem(
        getSidebarThemeKey(currentUserId, currentOrgId),
        theme,
      )

      // Save at user scope so it follows this user across devices inside the
      // current organization without affecting other members.
      await saveThemePreference({ sidebarTheme: theme })
    },
    [saveThemePreference, currentOrgId, currentUserId, setUISidebarTheme],
  )

  return {
    themeMode,
    sidebarTheme,
    isDarkMode,
    currentSidebarColor,
    setThemeMode,
    setSidebarTheme,
    isLoading: storeIsLoading || !isStoreInitialized,
  }
}
